import { useMemo } from 'react';
import { Calendar, Clock, Layers } from 'lucide-react';
import type { VcfWorkstream } from '@/hooks/useWorkstreams';
import { cn } from '@/lib/utils';

interface GanttTimelineProps {
  workstreams: VcfWorkstream[];
  selectedCapabilities: Set<string>;
  guardrailValues: Record<string, string>;
  daysPerWeek?: number;
  numWeeks?: number;
}

const NUM_WEEKS = 44;

export function GanttTimeline({
  workstreams,
  selectedCapabilities,
  guardrailValues,
  daysPerWeek = 5,
  numWeeks = NUM_WEEKS,
}: GanttTimelineProps) {
  const timelineData = useMemo(() => {
    const workstreamTimelines: Array<{
      id: string;
      name: string;
      days: number;
      hours: number;
      startWeek: number;
      endWeek: number;
      capabilities: number;
    }> = [];

    let currentStartWeek = 1;
    let totalDays = 0;
    let totalCapabilities = 0;

    for (const ws of workstreams) {
      let wsDays = 0;
      let wsCapabilities = 0;

      for (const cap of ws.capabilities || []) {
        if (!selectedCapabilities.has(cap.id)) continue;

        wsCapabilities++;

        // Calculate guardrail multiplier for this capability
        let guardrailFactor = 1;
        for (const gr of cap.guardrails || []) {
          const inputVal = guardrailValues[gr.id];
          if (inputVal && gr.multiplier) {
            const match = gr.multiplier.match(/\+?(\d+\.?\d*)x?\s*per/i);
            if (match) {
              const multiplierPerUnit = parseFloat(match[1]);
              const baseline = parseFloat(gr.baseline || '1');
              const current = parseFloat(inputVal);
              if (!isNaN(current) && !isNaN(baseline)) {
                guardrailFactor += multiplierPerUnit * Math.max(0, current - baseline);
              }
            }
            const boolMatch = gr.multiplier.match(/\+?(\d+\.?\d*)\s*days?\s*if/i);
            if (boolMatch && inputVal === 'true') {
              guardrailFactor += parseFloat(boolMatch[1]) / 10;
            }
          }
        }

        // Sum activities
        for (const act of cap.activities || []) {
          const baseDays = (act.baseDays || 0) / 100;
          wsDays += baseDays * guardrailFactor;
        }
      }

      if (wsDays > 0) {
        const wsWeeks = Math.max(1, Math.ceil(wsDays / daysPerWeek));
        const endWeek = Math.min(currentStartWeek + wsWeeks - 1, numWeeks);

        workstreamTimelines.push({
          id: ws.id,
          name: ws.name,
          days: wsDays,
          hours: wsDays * 8,
          startWeek: currentStartWeek,
          endWeek,
          capabilities: wsCapabilities,
        });

        totalDays += wsDays;
        totalCapabilities += wsCapabilities;

        // Overlap workstreams by approximately 30-50%
        currentStartWeek += Math.max(1, Math.ceil(wsWeeks * 0.5));
      }
    }

    const totalDuration = workstreamTimelines.length > 0
      ? Math.max(...workstreamTimelines.map(ws => ws.endWeek))
      : 0;

    return {
      workstreams: workstreamTimelines,
      totalDays,
      totalDuration,
      totalWorkstreams: workstreamTimelines.length,
      totalCapabilities,
    };
  }, [workstreams, selectedCapabilities, guardrailValues, daysPerWeek, numWeeks]);

  if (selectedCapabilities.size === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        <Calendar className="h-12 w-12 mx-auto text-gray-300 mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">No capabilities selected</h3>
        <p className="text-sm">Select capabilities from the Capabilities tab to see the timeline.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-heading text-lg font-semibold text-xtravirt-dark">
            Project Timeline
          </h3>
          <p className="text-sm text-gray-500 mt-1">
            Visual timeline of workstream execution
          </p>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="gantt-summary">
        <div className="gantt-summary-stat">
          <div className="gantt-summary-label">
            <Calendar className="inline h-3.5 w-3.5 mr-1" />
            Duration
          </div>
          <div className="gantt-summary-value">{timelineData.totalDuration} weeks</div>
        </div>
        <div className="gantt-summary-stat">
          <div className="gantt-summary-label">
            <Clock className="inline h-3.5 w-3.5 mr-1" />
            Total Days
          </div>
          <div className="gantt-summary-value">{timelineData.totalDays.toFixed(1)}</div>
        </div>
        <div className="gantt-summary-stat">
          <div className="gantt-summary-label">
            <Layers className="inline h-3.5 w-3.5 mr-1" />
            Workstreams
          </div>
          <div className="gantt-summary-value">{timelineData.totalWorkstreams}</div>
        </div>
      </div>

      {/* Gantt Chart */}
      <div className="gantt-container card overflow-hidden !p-0">
        <table className="gantt-table">
          <thead>
            <tr>
              <th className="gantt-row-label">Workstream</th>
              {Array.from({ length: numWeeks }, (_, i) => (
                <th key={i} className="gantt-week-header">
                  W{i + 1}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {timelineData.workstreams.map((ws) => (
              <tr key={ws.id}>
                <td className="gantt-row-label">
                  <div className="gantt-row-title">{ws.name}</div>
                  <div className="gantt-row-meta">
                    {ws.days.toFixed(1)} days &middot; {ws.capabilities} cap{ws.capabilities !== 1 ? 's' : ''}
                  </div>
                </td>
                {Array.from({ length: numWeeks }, (_, i) => {
                  const week = i + 1;
                  const isInRange = week >= ws.startWeek && week <= ws.endWeek;
                  const isStart = week === ws.startWeek;
                  const isEnd = week === ws.endWeek;

                  return (
                    <td key={i} className="gantt-cell">
                      {isInRange && (
                        <div
                          className={cn(
                            'gantt-bar',
                            isStart && 'gantt-bar-start',
                            isEnd && 'gantt-bar-end',
                            !isStart && !isEnd && 'gantt-bar-middle'
                          )}
                          title={`${ws.name}: W${ws.startWeek}-W${ws.endWeek} (${ws.days.toFixed(1)} days)`}
                        />
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
            {/* Total timeline indicator row */}
            <tr className="bg-gray-50">
              <td className="gantt-row-label">
                <div className="gantt-row-title font-semibold">Project Total</div>
                <div className="gantt-row-meta">
                  {timelineData.totalDays.toFixed(1)} days &middot; {timelineData.totalDuration} weeks
                </div>
              </td>
              {Array.from({ length: numWeeks }, (_, i) => {
                const week = i + 1;
                const hasActivity = timelineData.workstreams.some(
                  ws => week >= ws.startWeek && week <= ws.endWeek
                );

                return (
                  <td key={i} className="gantt-cell">
                    {hasActivity && (
                      <div
                        className="absolute inset-y-1 rounded bg-xtravirt-dark/20"
                        style={{ width: '100%' }}
                      />
                    )}
                  </td>
                );
              })}
            </tr>
          </tbody>
        </table>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 text-xs text-gray-500">
        <div className="flex items-center gap-2">
          <div className="w-6 h-3 rounded" style={{ background: 'linear-gradient(135deg, var(--accent) 0%, var(--success) 100%)' }} />
          <span>Workstream execution</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-6 h-3 rounded bg-xtravirt-dark/20" />
          <span>Project activity period</span>
        </div>
      </div>
    </div>
  );
}
